/**
 * Data Pool Engine v1 — API client.
 * Every call hits the new /api/datapool/* backend. No legacy /data-pool shim.
 */
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API_BASE });
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("sv_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("sv_token");
      localStorage.removeItem("sv_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

/** Create a new empty working batch. */
export const createBatch = () => client.post("/datapool/batches", {});

/** Upload files into an existing batch. */
export const uploadBatchFiles = (batchId, files) => {
  const fd = new FormData();
  (files || []).forEach((f) => fd.append("files", f));
  return client.post(`/datapool/batches/${batchId}/upload`, fd);
};

/** Add a single typed-in manual financial entry to an existing batch.
 *  The record enters the same pipeline as uploaded data:
 *  Manual Entry → Normalize → Diagnose → Review → Commit → Ledger → Dashboard. */
export const addManualEntry = (batchId, payload) =>
  client.post(`/datapool/batches/${batchId}/manual-entry`, payload);

/** Fetch batch summary (state + counters). */
export const getBatch = (batchId) => client.get(`/datapool/batches/${batchId}`);
export const getBatchStatus = (batchId) => client.get(`/datapool/batches/${batchId}/status`);
export const getBatchParserDiagnostics = (batchId) => client.get(`/datapool/batches/${batchId}/parser-diagnostics`);

/** Forensic diagnostic — why was this batch routed to review/failed?
 *  Returns ``pipeline_message``, ``file_processing_errors`` rows and
 *  per-file ``document_lodges`` so the UI can show the EXACT reason
 *  instead of a generic "Document Intake Failed" badge. */
export const getBatchDiagnostics = (batchId) =>
  client.get(`/datapool/batches/${batchId}/diagnostics`);

/** Fetch files list for batch. */
export const getBatchFiles = (batchId) => client.get(`/datapool/batches/${batchId}/files`);

/** Fetch all records in batch. */
export const getBatchRecords = (batchId) => client.get(`/datapool/batches/${batchId}/records`);

/** Start async AI diagnostic — returns {run_id, run_status:"running"}. */
export const runBatchDiagnostic = (batchId) => client.post(`/datapool/batches/${batchId}/diagnose`, {});

/** Poll a diagnostic run. */
export const getRun = (runId) => client.get(`/datapool/runs/${runId}`);

/** Fetch records that were analysed in a specific run. */
export const getRunRecords = (runId) => client.get(`/datapool/runs/${runId}/records`);

/** Edit normalized fields on a record. override_reason is required. */
export const editRecord = (recordId, payload) => client.put(`/datapool/records/${recordId}`, payload);

/** Recompute classification + allocation for one record. */
export const recalculateRecord = (recordId, review) =>
  client.post(`/datapool/records/${recordId}/recalculate`, review || {});

/** Approve a record (moves status → approved). */
export const approveRecord = (recordId, review) =>
  client.post(`/datapool/records/${recordId}/approve`, review || {});

/** Manual vault allocation override. allocations must sum to reporting_amount. */
export const manualAllocateRecord = (recordId, payload) =>
  client.post(`/datapool/records/${recordId}/manual-allocate`, payload);

/** Fetch full record history chain. */
export const getRecordHistory = (recordId) => client.get(`/datapool/records/${recordId}/history`);

/** Commit approved records (approved → committed). */
export const commitBatch = (batchId) => client.post(`/datapool/batches/${batchId}/commit`, {});

/** Apply committed records to dashboard (ledger + vault totals + narrative). */
export const applyBatch = (batchId) => client.post(`/datapool/batches/${batchId}/apply`, {});

/** Run AI Diagnostic across ALL pending records (every batch with not_analysed/failed records). */
export const diagnoseAllPending = () => client.post(`/datapool/diagnose-pending`, {});

/** Auto-allocate every pending record across every batch using ONLY the deterministic
 *  rules engine — no AI, instant. Records that match a keyword auto-apply; the rest
 *  are direction-defaulted at low confidence and flagged for manual review. */
export const allocateAllPendingRulesOnly = () => client.post(`/datapool/allocate-pending-rules-only`, {});

/** DRY RUN — return the proposed allocations without writing anything.
 *  Use this to show users what would happen before they commit. */
export const previewAllPendingRulesOnly = () => client.post(`/datapool/allocate-pending-rules-only/preview`, {});

/** Roll back every record touched by a given sweep_id. */
export const rollbackSweep = (sweepId) => client.post(`/datapool/allocations/rollback/${sweepId}`, {});

/** Auto-allocate a single batch using rules-only (no AI). */
export const allocateBatchRulesOnly = (batchId) => client.post(`/datapool/batches/${batchId}/allocate-rules-only`, {});

/** Document Archive — every batch ever uploaded by this org. */
export const getArchive = () => client.get(`/datapool/archive`);

/** Records for any historical batch — same shape as the live batch endpoint. */
export const getArchivedRecords = (batchId) => client.get(`/datapool/archive/${batchId}/records`);

/** Re-classify every non-locked, non-committed record in a historical batch.
 *  mode: "rules_only" (instant, no AI) or "rules_plus_ai" (slower, smarter). */
export const reclassifyArchivedBatch = (batchId, mode = "rules_only") =>
  client.post(`/datapool/archive/${batchId}/reclassify`, { mode });
